import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAccountProfile } from '../services/apis/tradingAccountProfile'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../selectors/user'
import {
    findBesseggenAccount,
    findPracticeAccount,
    getAccountSwitcherMode,
    hasActiveTradingAccount,
    isBesseggenAccountSwitchable
} from '../util/tradingAccountContext'
import { isPracticeTradeAccountEnabled, isRealTradingAccount } from '../util/tradingAccounts'

export const useTradingAccountContext = () => {
    const tradingAccounts = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const [profileClientCode, setProfileClientCode] = useState<string>()

    const practiceAccount = useMemo(
        () => findPracticeAccount(tradingAccounts ?? []),
        [tradingAccounts]
    )
    const besseggenAccount = useMemo(
        () => findBesseggenAccount(tradingAccounts ?? []),
        [tradingAccounts]
    )
    const hasRealAccount = useMemo(
        () => (tradingAccounts ?? []).some((account) => isRealTradingAccount(account.brokerCode)),
        [tradingAccounts]
    )

    useEffect(() => {
        if (!hasRealAccount) {
            setProfileClientCode(undefined)
            return
        }

        let cancelled = false
        void getAccountProfile()
            .then((profile) => {
                if (!cancelled) {
                    setProfileClientCode(profile.tradingAccount?.clientCode?.trim() || undefined)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProfileClientCode(undefined)
                }
            })

        return () => {
            cancelled = true
        }
    }, [hasRealAccount, besseggenAccount?.accountId, besseggenAccount?.status])

    const practiceEnabled = isPracticeTradeAccountEnabled(practiceAccount?.status)
    const besseggenActive = isBesseggenAccountSwitchable(besseggenAccount?.status ?? 0)
    const mode = getAccountSwitcherMode(practiceAccount, besseggenAccount)
    const activeTradingAccount = hasActiveTradingAccount(practiceAccount, besseggenAccount)

    return {
        tradingAccounts: tradingAccounts ?? [],
        primaryTradingAccount,
        practiceAccount,
        besseggenAccount,
        practiceEnabled,
        besseggenActive,
        mode,
        profileClientCode,
        hasAnyAccount: !!practiceAccount || !!besseggenAccount,
        hasActiveTradingAccount: activeTradingAccount
    }
}
